import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { sendWelcomeEmail } from '../services/emailService';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const WelcomeModal = ({ isOpen, onClose }: WelcomeModalProps) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await sendWelcomeEmail({ email });
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000); // Cerrar después de 2 segundos de éxito
    } catch (err) {
      setError('Ha ocurrido un error al enviar el email. Por favor, inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
            className="relative bg-white rounded-lg p-8 shadow-xl z-10 w-11/12 max-w-lg"
          >
            <div className="relative">
              <button
                onClick={onClose}
                className="absolute -top-4 -right-4 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-lg text-gray-500 hover:text-gray-700 transition-colors"
              >
                ✕
              </button>
              
              <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">¡Bienvenido a HumanCall!</h2>
              
              <div className="mb-8 text-center">
                <p className="text-xl font-semibold text-gray-800 mb-4">
                  ANALIZAMOS TU NECESIDAD COMPLETAMENTE GRATIS Y SIN COMPROMISO ALGUNO
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Tu correo electrónico"
                    required
                    disabled={isLoading || success}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-100"
                  />
                </div>

                {error && (
                  <p className="text-red-500 text-sm text-center">{error}</p>
                )}

                {success ? (
                  <div className="text-center text-green-600">
                    <p className="font-semibold">¡Email enviado con éxito!</p>
                    <p className="text-sm">Nos pondremos en contacto contigo pronto.</p>
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-lg font-semibold uppercase disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enviando...
                      </span>
                    ) : (
                      'Vale, lo quiero'
                    )}
                  </button>
                )}

                <p className="text-sm text-gray-500 text-center mt-4">
                  Tu información está segura con nosotros. No compartimos tus datos con terceros.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}; 